import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ReminderRow {
  readonly id: string;
  readonly name: string;
  readonly frequency: string;
  readonly status: string;
  readonly nextRunAt: string;
}

export function NotificationsRemindersPage(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ReminderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [reminderCode, setReminderCode] = useState('');
  const [reminderName, setReminderName] = useState('');
  const [eventType, setEventType] = useState('');
  const [targetEntityType, setTargetEntityType] = useState('');
  const [frequency, setFrequency] = useState('DAILY');
  const [intervalMinutes, setIntervalMinutes] = useState('');
  const [maxReminders, setMaxReminders] = useState('');
  const [channels, setChannels] = useState('email');
  const [nextTriggerAt, setNextTriggerAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ items: ReminderRow[] }>('/notifications/reminders', { token })
      .then((r) => setRows(r.items))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      const channelList = channels.split(/[\s,]+/).filter(Boolean);
      await apiRequest('/notifications/reminders', {
        method: 'POST',
        token,
        body: {
          reminderCode,
          reminderName,
          eventType,
          targetEntityType,
          frequency,
          intervalMinutes: intervalMinutes ? Number(intervalMinutes) : undefined,
          maxReminders: maxReminders ? Number(maxReminders) : undefined,
          channels: channelList.length > 0 ? channelList : undefined,
          nextTriggerAt: nextTriggerAt || undefined,
        },
      });
      setCreated(true);
      setReminderCode('');
      setReminderName('');
      setEventType('');
      setTargetEntityType('');
      setIntervalMinutes('');
      setMaxReminders('');
      setNextTriggerAt('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const schedule = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/notifications/reminders/${id}/schedule`, { token, method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/notifications/reminders/${id}/cancel`, { token, method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="reminders">
      <h1>{t('notif.reminders.title')}</h1>

      <form onSubmit={onCreate} aria-label="reminder-create-form">
        <h2>{t('notif.reminders.newReminder')}</h2>
        <label htmlFor="rm-code">{t('notif.reminders.reminderCode')}</label>
        <input id="rm-code" value={reminderCode} onChange={(e) => setReminderCode(e.target.value)} required />
        <label htmlFor="rm-name">{t('notif.reminders.reminderName')}</label>
        <input id="rm-name" value={reminderName} onChange={(e) => setReminderName(e.target.value)} required />
        <label htmlFor="rm-event">{t('notif.reminders.eventType')}</label>
        <input id="rm-event" value={eventType} onChange={(e) => setEventType(e.target.value)} required />
        <label htmlFor="rm-entity">{t('notif.reminders.targetEntityType')}</label>
        <input id="rm-entity" value={targetEntityType} onChange={(e) => setTargetEntityType(e.target.value)} required />
        <label htmlFor="rm-frequency">{t('notif.reminders.frequency')}</label>
        <select id="rm-frequency" aria-label="reminder-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
          <option value="ONCE">{t('notif.reminders.freqOnce')}</option>
          <option value="HOURLY">{t('notif.reminders.freqHourly')}</option>
          <option value="DAILY">{t('notif.reminders.freqDaily')}</option>
          <option value="WEEKLY">{t('notif.reminders.freqWeekly')}</option>
          <option value="MONTHLY">{t('notif.reminders.freqMonthly')}</option>
        </select>
        <label htmlFor="rm-interval">{t('notif.reminders.interval')}</label>
        <input id="rm-interval" type="number" value={intervalMinutes} onChange={(e) => setIntervalMinutes(e.target.value)} />
        <label htmlFor="rm-max">{t('notif.reminders.maxReminders')}</label>
        <input id="rm-max" type="number" value={maxReminders} onChange={(e) => setMaxReminders(e.target.value)} />
        <label htmlFor="rm-channels">{t('notif.common.channels')}</label>
        <input id="rm-channels" value={channels} onChange={(e) => setChannels(e.target.value)} />
        <label htmlFor="rm-next">{t('notif.reminders.firstTrigger')}</label>
        <DateInput id="rm-next" type="datetime-local" value={nextTriggerAt} onValueChange={(nextValue) => setNextTriggerAt(nextValue)} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('notif.reminders.created')}</p> : null}
        <Button type="submit" disabled={creating} variant="primary">{creating ? t('notif.common.creating') : t('notif.reminders.createReminder')}</Button>
      </form>

      <DataGrid<any>
        caption="reminders-table"
        rows={rows}
        getRowId={(r) => r.id}
        columns={[
          { key: 'name', header: t('notif.reminders.colName'), value: (r) => r.name },
          { key: 'frequency', header: t('notif.reminders.frequency'), value: (r) => r.frequency },
          { key: 'nextRun', header: t('notif.reminders.nextRun'), value: (r) => r.nextRunAt, render: (r) => at(r.nextRunAt) },
          { key: 'status', header: t('notif.common.status'), value: (r) => r.status },
        ]}
        actionsHeader={t('notif.common.action')}
        actions={(r) => (
          <>
            {r.status === 'DRAFT' && (
              <Button type="button" disabled={busyId === r.id} onClick={() => void schedule(r.id)} variant="secondary">
                {busyId === r.id ? t('notif.reminders.scheduling') : t('notif.reminders.schedule')}
              </Button>
            )}
            {r.status === 'ACTIVE' && (
              <Button type="button" disabled={busyId === r.id} onClick={() => void cancel(r.id)} variant="danger">
                {busyId === r.id ? t('notif.reminders.cancelling') : t('notif.common.cancel')}
              </Button>
            )}
          </>
        )}
      />
    </main>
  );
}
